<template>
    <div v-loading="isBusy" class="wrap">
        <div class="d-flex">
          <div class="mr-auto">
              <button class="btn btn-secondary" @click="refresh"><i class="fas fa-sync-alt"></i></button>
              <slot name="buttons"></slot>
          </div>
          <div class="align-self-end">
              <input-debounced
                  v-model="search"
                  :clearable="true"
                  :autofocus="true"
                  placeholder="Search"
                  class="search-bar w-300 mb-2"
              ></input-debounced>
          </div>
        </div>
        <base-table
            :base-url="urlWithFilters"
            ref="baseTable"
            @load-success="onLoadSuccess"
            @refresh="onRefresh"
            :current-page="currentPage"
            v-loading="isRefreshing"
            element-loading-spinner="el-icon-loading"
        >
            <template v-slot>
                <el-table
                    :data="items"
                    stripe
                    class="w-100"
                >
                  <el-table-column
                      prop="model.id"
                      label="Id"
                      min-width="80"
                  ></el-table-column>
                  <el-table-column
                      prop="model.user.full_name"
                      label="Owner"
                      min-width="150"
                  ></el-table-column>
                  <el-table-column
                      prop="model.brand.title"
                      label="Brand"
                      min-width="100"
                  ></el-table-column>
                  <el-table-column
                      prop="model.name"
                      label="Name"
                      min-width="250"
                  ></el-table-column>
                  <el-table-column
                      prop="model.expiry_period"
                      label="Expiry Period"
                      min-width="120"
                  ></el-table-column>
                  <el-table-column
                      prop="model.conversations_count"
                      label="Conversations"
                      min-width="120"
                  ></el-table-column>
                  <el-table-column
                      prop="model.note"
                      label="Notes"
                      min-width="150"
                      v-slot="{row}"
                  >
                      <note-column :row="row" @show-list="onShowNoteList" @stored="onNoteStored"></note-column>
                  </el-table-column>
                  <el-table-column
                      label="Group Messages"
                      min-width="150"
                      v-slot="{row}"
                      class-name="overflow-show"
                  >
                      <bulk-messages-column
                          :row="row"
                          @index-bulk-messages="onIndexBulkMessages"
                          @create-bulk-messages="onCreateBulkMessages"
                          @create-email-bulk-messages="onCreateEmailBulkMessages"
                          @show-quick-message="onShowQuickMessage"
                      ></bulk-messages-column>
                  </el-table-column>
                  <el-table-column
                      label="Actions"
                      v-slot="{row}"
                      min-width="120"
                  >
                      <actions-column
                          :row="row"
                          @edit="onEdit"
                          @view="onView"
                          @delete="onDelete"
                          @restore="onRestore"
                      ></actions-column>
                  </el-table-column>
                </el-table>
            </template>
        </base-table>
        <el-pagination
            layout="prev, pager, next"
            :total="total"
            :current-page.sync="currentPage"
            :page-size="pageSize"
            class="text-right"
        ></el-pagination>
        <dialog-note-list></dialog-note-list>
    </div>
</template>

<script>
import TableWithPagination from '../table-with-pagination';
import {addQueryParams} from '../../libs/url';
import ActionsColumn from './actions-column';
import BulkMessagesColumn from './columns/bulk-messages-column';
import eventBus from '../../event-bus';
import NoteColumn from './columns/note-column';
import DialogNoteList from '../send-list-note/dialog-note-list';

export default {
  extends: TableWithPagination,

  components: {
    NoteColumn,
    ActionsColumn,
    BulkMessagesColumn,
    DialogNoteList,
  },

  data() {
    return {
      search: null,
      filters: {},
      isBusy: false,
    };
  },

  computed: {
    /**
     * @return {string}
     */
    urlWithFilters() {
      return addQueryParams(this.url, this.filters);
    },
  },

  watch: {
    search: function (value) {
      this.filters = value
          ? _.assign({}, this.filters, {search: value})
          : this.filters = _.omit(this.filters, ['search']);
    },
  },

  methods: {
    /**
     * @return {undefined}
     */
    refresh() {
      this.$refs.baseTable.refresh();
    },

    /**
     * @param {string} url
     */
    onEdit({url}) {
      window.location = url;
    },

    /**
     * @param {string} url
     */
    onView({url}) {
      window.location = url;
    },

    /**
     * @param {string} url
     */
    onRestore({url}) {
      this.isBusy = true;
      axios.post(url, {_method: 'POST'})
          .then(({data: {message}}) => {
            _.isString(message) && this.$message.success(message);
            this.refresh();
          })
          .catch((err) => {
            const message = _.get(err, 'response.data.message');
            _.isString(message) && this.$message.error(message);
          })
          .then(() => this.isBusy = false);
    },

    /**
     * @param {string} url
     */
    onDelete({url}) {
      this.isBusy = true;
      axios.post(url, {_method: 'DELETE'})
          .then(({data: {message}}) => {
            _.isString(message) && this.$message.success(message);
            this.refresh();
          })
          .catch((err) => {
            const message = _.get(err, 'response.data.message');
            _.isString(message) && this.$message.error(message);
          })
          .then(() => this.isBusy = false);
    },

    /**
     * @param {string} url
     */
    onIndexBulkMessages({url}) {
      window.location = url;
    },

    /**
     * @param {string} url
     */
    onCreateBulkMessages({url}) {
      window.location = url;
    },

    /**
     * @param {string} url
     */
    onCreateEmailBulkMessages({url}) {
      eventBus.$emit('show-email-bulk-message-dialog', url);
    },

    /**
     * @param {string} url
     * @event show-quick-bulk-message
     */
    onShowQuickMessage({url}) {
      eventBus.$emit('show-quick-bulk-message', url);
    },

    /**
     * @param {string} url
     * @event show-note-index-dialog
     */
    onShowNoteList({url}) {
      eventBus.$emit('show-send-list-note-index-dialog', url);
    },

    /**
     * @param {number} index
     * @param {object} model
     */
    onNoteStored({index, model}) {
      this.$set(this.items[index].model, 'note', model);
    },
  },
}
</script>

<style scoped>
  /deep/ .overflow-show .cell {
    overflow: visible;
  }

  /deep/ .el-table {
    overflow: visible;
  }

  /deep/ .el-table__body-wrapper {
    overflow: visible;
  }
</style>
